import * as React from "react";
import { type GetServerSideProps, type NextPage } from "next";
import { publicSerif as accessSerif } from "~/styles/fonts/publicSerif";
import Head from "next/head";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  MarketingFooter,
  MarketingNav,
  publicPageShellClassName,
} from "~/components/public-marketing-chrome";
import CheckoutOpeningControl from "~/components/CheckoutOpeningControl";
import { NewsletterSignupLink } from "~/components/newsletter/NewsletterSignupLink";
import {
  formatUsdCents,
  isConfiguredLifetimeCheckoutPlan,
  isConfiguredPremiumCheckoutPlan,
  isPremiumAccessPlanKey,
  premiumAccessPlanKeys,
  premiumAccessPlans,
  type PremiumAccessPlanKey,
} from "~/lib/accessPlans";
import {
  parseCheckoutOfferKey,
  publicCheckoutOfferFromFacts,
  type PublicCheckoutOffer,
} from "~/lib/checkoutCatalog";
import { type CheckoutOpeningMode } from "~/lib/checkoutOpeningMode";
import {
  externalCheckoutTargetFromQuery,
  whopCheckoutProxyOrigin,
} from "~/lib/whopCheckoutProxy";
import {
  decideAgreementsEntry,
  decideDurableAgreementsReentry,
  resolveExpiredCheckoutRecoverySessionId,
  resolveAgreementsReentrySessionId,
  resolveStripeCheckoutRequest,
  stripeSessionReadNeedsDurableRecovery,
  type StripeCheckoutRequest,
} from "~/server/stripeCheckout/getAccessResolver";
import { resolvePromotionCodeIntent } from "~/server/stripeCheckout/promo";
import {
  checkoutProvider,
  stripePublishableKey,
} from "~/server/stripeCheckout/plans";
import { stripeGetCheckoutSession } from "~/server/stripeCheckout/stripeApi";
import { isReleaseHeld, releaseApprovedFor } from "~/lib/leviathanGateAdmin";
import {
  buildCheckoutEntries,
  checkoutCatalogUnavailableServerSideProps,
  setCheckoutNoStore,
} from "~/server/stripeCheckout/accessPageServerProps";
import HeadComponent from "~/ui/head";
import PricingPlans from "~/components/public-marketing/PricingPlans";
import pricing from "~/components/public-marketing/PricingPlans.module.css";
import type { AccessInquiryPlan } from "~/components/access/AccessInquiryModal";

const StripeCheckoutModal = dynamic(
  () => import("~/components/StripeCheckoutModal")
);
const CheckoutModal = dynamic(
  () => import("~/components/WhopCheckoutModal")
);

const NewsletterPreferencesModal = dynamic(
  () =>
    import("~/components/newsletter/NewsletterPreferencesModal").then(
      (module) => module.NewsletterPreferencesModal
    ),
  { ssr: false }
);
const NewsletterStartModal = dynamic(
  () =>
    import("~/components/newsletter/NewsletterStartModal").then(
      (module) => module.NewsletterStartModal
    ),
  { ssr: false }
);
const NewsletterVerifyModal = dynamic(
  () =>
    import("~/components/newsletter/NewsletterVerifyModal").then(
      (module) => module.NewsletterVerifyModal
    ),
  { ssr: false }
);

const AccessInquiryModal = dynamic(
  () =>
    import("~/components/access/AccessInquiryModal").then(
      (module) => module.AccessInquiryModal
    ),
  { ssr: false }
);

export type OfferAvailability = "available" | "waitlist" | "closed";

const pageTitle = "TWT | Pricing";

type CheckoutPlanKey =
  | PremiumAccessPlanKey
  | "free"
  | "lifetime"
  | `plan_${string}`;

export type CheckoutTarget = {
  plan: CheckoutPlanKey;
  title: string;
  url: string;
  external: boolean;
};

export type AccessPageProps = {
  signupCompleteHref?: "/dashboard" | "/signup-complete";
  openingMode: CheckoutOpeningMode;
  checkoutOrigin: string;
  checkoutEntries: Record<PremiumAccessPlanKey, CheckoutTarget>;
  /** Active published catalog projected to browser-safe presentation only. */
  publicCheckoutOffers: PublicCheckoutOffer[];
  /** A provider/database failure, distinct from an intentionally empty catalog. */
  catalogUnavailable: boolean;
  initialCheckout: CheckoutTarget | null;
  // True when new signups go through our own Stripe checkout modal
  // (CHECKOUT_PROVIDER=stripe, fully configured). Decided server-side and
  // threaded through props to avoid the NEXT_PUBLIC_ hydration trap noted in
  // accessPlans.ts.
  stripeCheckoutEnabled: boolean;
  // Browser-safe publishable key for the modal's Payment Element.
  stripePublishableKey: string | null;
  // Resolver result: a share link (?checkout=1&plan=<key>[&code=…]), a bare
  // plan_… id, or a pasted whop.com/checkout URL that reverse-maps to a
  // premium tier opens the same Stripe modal with that tier (and discount)
  // preselected. The modal validates buyer identity before creating payment.
  initialStripeCheckout: StripeCheckoutRequest | null;
  // Agreements continuation/re-entry (?agreements=1&session_id=cs_…): a
  // completed paid or reservation session opens the checkout modal directly
  // at its agreements step. The reservation flag comes only from the
  // server-verified Stripe Session mode.
  initialStripeAgreements: {
    sessionId: string;
    offer: PublicCheckoutOffer | null;
    reservation: boolean;
  } | null;
  /** Server-read Premium offer state. Waitlist renders the §101.2 reserve CTA (P12.4). */
  premiumAvailability: OfferAvailability | null;
  // A.6: on a ?resume=setup open the member has already paid, so there is no
  // checkout target to carry an offer. Their own plan (from the newest
  // commerce_subscriptions row) is resolved server-side so the modal has an
  // offer to render and can land on the first unfinished room. Null when the
  // member or plan cannot be resolved.
  resumeOffer: PublicCheckoutOffer | null;
  /**
   * The signed-in visitor's own plan; that card's CTA greys out to "Your
   * current plan". Null for signed-out visitors, who see live CTAs everywhere.
   */
  currentPlan: "free" | "premium" | null;
};

function firstQueryValue(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function stripeExpandableObjectId(
  value: string | { id?: string } | null | undefined
) {
  return typeof value === "string"
    ? value
    : typeof value?.id === "string"
    ? value.id
    : null;
}

// A.6: map a stored commerce_subscriptions canonical plan (hyphen keys today,
// underscore keys on older rows) to the checkout plan key so a resume open can
// present the member's own tier.
function resumePlanKeyFromCanonical(
  canonical: string | null | undefined
): PremiumAccessPlanKey | null {
  switch (canonical) {
    case "premium-monthly":
    case "premium_monthly":
      return "premium-monthly";
    case "premium-quarterly":
    case "premium_quarterly":
      return "premium-quarterly";
    case "premium-annual":
    case "premium_annual":
    case "premium_yearly":
      return "premium-annual";
    default:
      return null;
  }
}

function resumePremiumOffer(
  planKey: PremiumAccessPlanKey
): PublicCheckoutOffer | null {
  const plan = premiumAccessPlans[planKey];
  return publicCheckoutOfferFromFacts({
    key: planKey,
    slug: planKey,
    name: `Premium ${plan.label}`,
    description: plan.note,
    offerVersionMarker: `resume-${planKey}`,
    amountCents: plan.amountCents,
    currency: "usd",
    billingInterval: planKey === "premium-annual" ? "year" : "month",
    billingIntervalCount: planKey === "premium-quarterly" ? 3 : 1,
  });
}

/** Browser presentation for the dormant legacy Whop rail only. */
function legacyWhopPremiumOffers(): PublicCheckoutOffer[] {
  return premiumAccessPlanKeys.flatMap((key) => {
    const plan = premiumAccessPlans[key];
    const offer = publicCheckoutOfferFromFacts({
      key,
      slug: key,
      name: `Premium ${plan.label}`,
      description: plan.note,
      offerVersionMarker: `legacy-whop-${key}`,
      amountCents: plan.amountCents,
      currency: "usd",
      billingInterval: key === "premium-annual" ? "year" : "month",
      billingIntervalCount: key === "premium-quarterly" ? 3 : 1,
    });
    return offer ? [offer] : [];
  });
}

function checkoutTargetFromQuery(
  query: Record<string, unknown>
): string | null {
  const requestedPlan = firstQueryValue(
    query.plan as string | string[] | undefined
  );
  if (requestedPlan) return requestedPlan;
  const checkout = firstQueryValue(
    query.checkout as string | string[] | undefined
  );
  if (checkout && checkout !== "1") return checkout;
  return (
    firstQueryValue(query.checkout_url as string | string[] | undefined) ?? null
  );
}

function providerTargetImpliesCheckout(target: string | null): boolean {
  return Boolean(
    target &&
      (/^(?:plan|plink)_[A-Za-z0-9]+$/.test(target) ||
        /^cs_live_[A-Za-z0-9_]+$/.test(target) ||
        /^(https?:\/\/)?(?:www\.)?whop\.com\//i.test(target) ||
        /^(https?:\/\/)?(?:buy|checkout)\.stripe\.com\//i.test(target))
  );
}

export const getServerSideProps: GetServerSideProps<AccessPageProps> = async (
  context
) => {
  setCheckoutNoStore(context.res);
  // Premium uses the authoritative database offer availability below. The
  // older environment switch remains on the separate lifetime pages only.
  const openingMode: CheckoutOpeningMode = "buyable";
  const stripeCheckoutEnabled = checkoutProvider() === "stripe";
  const checkoutEntries = buildCheckoutEntries(!stripeCheckoutEnabled);
  let premiumAvailability: OfferAvailability | null = null;
  try {
    const { getPremiumAvailability } = await import("~/server/commerce/offers");
    premiumAvailability = await getPremiumAvailability();
  } catch (error) {
    console.error(
      `[checkout] premium availability unavailable: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
  const catalogUnavailableResult = () =>
    checkoutCatalogUnavailableServerSideProps(
      context,
      openingMode,
      premiumAvailability
    );
  const agreementsSessionId = resolveAgreementsReentrySessionId({
    stripeCheckoutEnabled,
    agreementsParam: firstQueryValue(context.query.agreements),
    sessionIdParam: firstQueryValue(context.query.session_id),
  });
  const checkoutCandidate =
    externalCheckoutTargetFromQuery(context.query.plan) ??
    externalCheckoutTargetFromQuery(context.query.checkout) ??
    externalCheckoutTargetFromQuery(context.query.checkout_url);
  const requestedPlan = firstQueryValue(context.query.plan);
  const requestedTarget = checkoutTargetFromQuery(context.query);
  const checkoutRequested =
    firstQueryValue(context.query.checkout) === "1" ||
    providerTargetImpliesCheckout(requestedTarget);
  const rootPromotionIntent = resolvePromotionCodeIntent([
    context.query.code,
    context.query.twt_promo,
  ]);
  // A supplied promotion is purchase intent. Never turn malformed, repeated,
  // or conflicting intent into an ordinary storefront whose next click buys
  // at full price.
  if (checkoutRequested && !rootPromotionIntent.ok) {
    return { notFound: true };
  }

  // A payment return is resolved before today's catalog. The exact Session
  // and its durable purchase claim already carry the checkout snapshot, so a
  // catalog-listing outage must not strand a paid buyer before signing.
  let agreementsEntry: "agreements" | "checkout-retry" | "blocked" | null =
    null;
  let agreementsOfferKey: ReturnType<typeof parseCheckoutOfferKey> = null;
  let agreementsOfferVersionMarker: string | null = null;
  let agreementsReservation = false;
  let agreementsRecoveredFromClaim = false;
  let expiredCheckoutRecoverySessionId: string | null = null;

  if (agreementsSessionId) {
    const session = await stripeGetCheckoutSession(agreementsSessionId);
    if (session.ok) {
      agreementsReservation = session.value.mode === "setup";
      if (agreementsReservation) {
        const { getPurchaseClaimByCheckoutSessionId } = await import(
          "~/server/stripeCheckout/purchaseClaims"
        );
        const claim = await getPurchaseClaimByCheckoutSessionId(
          agreementsSessionId
        );
        if (
          !claim ||
          claim.stripeCheckoutSessionId !== agreementsSessionId ||
          claim.sessionMode !== "setup"
        ) {
          return { notFound: true };
        }
        agreementsOfferKey = claim.planKey;
        agreementsOfferVersionMarker = claim.checkoutOfferVersion;
        agreementsRecoveredFromClaim = true;
      } else {
        agreementsOfferKey = parseCheckoutOfferKey(
          session.value.metadata?.twt_plan
        );
        agreementsOfferVersionMarker =
          session.value.metadata?.twt_offer_version?.trim() || null;
      }
      agreementsEntry = decideAgreementsEntry({
        sessionOk: true,
        paymentStatus: session.value.payment_status,
        offerKey: agreementsOfferKey,
      });
      if (agreementsEntry === "blocked") {
        // A well-formed but foreign/corrupt Session is blocked here. Never
        // route a payment return into the obsolete post-payment thanks page.
        return { notFound: true };
      }
      if (
        agreementsEntry === "checkout-retry" &&
        session.value.status === "expired"
      ) {
        try {
          const { getPurchaseClaimByCheckoutSessionId } = await import(
            "~/server/stripeCheckout/purchaseClaims"
          );
          const claim = await getPurchaseClaimByCheckoutSessionId(
            agreementsSessionId
          );
          expiredCheckoutRecoverySessionId =
            resolveExpiredCheckoutRecoverySessionId({
              requestedSessionId: agreementsSessionId,
              sessionStatus: session.value.status,
              paymentStatus: session.value.payment_status,
              sessionCustomerId: stripeExpandableObjectId(
                session.value.customer
              ),
              sessionSubscriptionId: stripeExpandableObjectId(
                session.value.subscription
              ),
              sessionMetadata: session.value.metadata,
              claim,
            });
          if (!expiredCheckoutRecoverySessionId) return { notFound: true };
        } catch (error) {
          console.error(
            `[checkout] expired Session recovery unavailable: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
          return catalogUnavailableResult();
        }
      }
    } else if (stripeSessionReadNeedsDurableRecovery(session.error)) {
      try {
        const [
          { getPurchaseClaimByCheckoutSessionId },
          { default: getUserFromCtx },
        ] = await Promise.all([
          import("~/server/stripeCheckout/purchaseClaims"),
          import("~/utils/getUserFromCtx"),
        ]);
        const claim = await getPurchaseClaimByCheckoutSessionId(
          agreementsSessionId
        );
        // A signed-out buyer may resume into the modal's Whop login step. An
        // already signed-in different buyer is rejected before any claim data
        // reaches the browser; the agreements API independently rechecks it.
        const currentUser = claim ? await getUserFromCtx(context) : null;
        const durableEntry = decideDurableAgreementsReentry({
          requestedSessionId: agreementsSessionId,
          currentWhopUserId: currentUser?.id ?? null,
          claim,
        });
        if (durableEntry !== "agreements") return { notFound: true };
        agreementsEntry = "agreements";
        agreementsOfferKey = claim?.planKey ?? null;
        agreementsOfferVersionMarker = claim?.checkoutOfferVersion ?? null;
        agreementsReservation = claim?.sessionMode === "setup";
        agreementsRecoveredFromClaim = true;
      } catch (error) {
        console.error(
          `[checkout] paid return recovery unavailable: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
        return catalogUnavailableResult();
      }
    } else {
      // An authoritative rejection (for example a 404) is not recoverable
      // provider ambiguity and must not be turned into claim authority.
      return { notFound: true };
    }
  }

  if (
    (!agreementsSessionId && requestedPlan === "lifetime") ||
    (!agreementsSessionId &&
      checkoutCandidate &&
      isConfiguredLifetimeCheckoutPlan(checkoutCandidate.plan))
  ) {
    return {
      redirect: {
        destination: "/lifetime?source=access-lifetime",
        permanent: false,
      },
    };
  }

  // The database catalog is authoritative for the Stripe rail. A read failure
  // must not resurrect a retired bootstrap Price. The legacy Whop rail keeps
  // its static three-option presentation until it is deliberately removed.
  let publicCheckoutOffers = legacyWhopPremiumOffers();
  let catalogUnavailable = false;
  let resolvedRequestedOffer: PublicCheckoutOffer | null = null;
  let resolveHistoricalOfferVersion:
    | ((marker: string) => Promise<{ publicOffer: PublicCheckoutOffer } | null>)
    | null = null;
  if (stripeCheckoutEnabled) {
    try {
      const catalog = await import("~/server/stripeCheckout/catalogCheckout");
      const { listPublicCheckoutOffers, resolvePublishedCheckoutOfferTarget } =
        catalog;
      resolveHistoricalOfferVersion =
        catalog.resolveHistoricalCheckoutOfferVersion;
      publicCheckoutOffers = await listPublicCheckoutOffers();
      if (publicCheckoutOffers.length === 0) {
        console.warn(
          "[checkout] public catalog read returned no published offers"
        );
      }
      if (
        (premiumAvailability === "available" ||
          premiumAvailability === "waitlist") &&
        !agreementsSessionId &&
        checkoutRequested &&
        requestedTarget
      ) {
        resolvedRequestedOffer =
          (await resolvePublishedCheckoutOfferTarget(requestedTarget))
            ?.publicOffer ?? null;
        if (!resolvedRequestedOffer) return { notFound: true };
      }
    } catch (error) {
      console.error(
        `[checkout] public catalog unavailable: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
      if (agreementsEntry === "agreements") {
        publicCheckoutOffers = [];
        catalogUnavailable = true;
        resolveHistoricalOfferVersion = null;
      } else {
        return catalogUnavailableResult();
      }
    }
  }

  let initialStripeCheckout = resolveStripeCheckoutRequest({
    stripeCheckoutEnabled,
    checkoutRequested:
      checkoutRequested &&
      (premiumAvailability === "available" ||
        premiumAvailability === "waitlist"),
    resolvedOffer: resolvedRequestedOffer,
    codeParam: context.query.code,
    legacyPromoParam: context.query.twt_promo,
  });

  // ── Agreements continuation / re-entry ────────────────────────────────────
  // ?agreements=1&session_id=cs_… is the return_url of every modal payment
  // AND the re-entry link for a paid member who closed the modal mid-
  // agreements. The session is read from Stripe server-side; nothing beyond
  // the id's shape is trusted from the URL. Outcomes:
  //   paid session, known plan   -> modal opens at the agreements step
  //   unpaid session, known plan -> checkout modal for that plan (payment
  //                                 retry after an abandoned 3DS redirect)
  //   ambiguous provider read    -> the exact durable bound claim resumes
  //   foreign/corrupt return     -> blocked; never the obsolete thanks page
  let initialStripeAgreements: AccessPageProps["initialStripeAgreements"] =
    null;
  if (agreementsSessionId && agreementsEntry) {
    let sessionOffer =
      !agreementsRecoveredFromClaim &&
      agreementsOfferKey &&
      agreementsOfferVersionMarker
        ? publicCheckoutOffers.find(
            (offer) =>
              offer.key === agreementsOfferKey &&
              offer.offerVersionMarker === agreementsOfferVersionMarker
          ) ?? null
        : null;
    if (
      !sessionOffer &&
      !agreementsRecoveredFromClaim &&
      agreementsOfferKey &&
      agreementsOfferVersionMarker &&
      resolveHistoricalOfferVersion
    ) {
      try {
        const historicalOffer = await resolveHistoricalOfferVersion(
          agreementsOfferVersionMarker
        );
        sessionOffer =
          historicalOffer?.publicOffer.key === agreementsOfferKey
            ? historicalOffer.publicOffer
            : null;
      } catch (error) {
        console.error(
          `[checkout] historical offer unavailable: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
        if (agreementsEntry !== "agreements") {
          return catalogUnavailableResult();
        }
      }
    }
    if (agreementsEntry === "agreements" && agreementsOfferKey) {
      initialStripeAgreements = {
        sessionId: agreementsSessionId,
        reservation: agreementsReservation,
        // The agreements API returns the immutable historical browser offer
        // from the claim. Passing null here is safe during a version rotation:
        // the modal waits for that exact response instead of consulting the
        // current catalog or static plan registry.
        offer: sessionOffer,
      };
    } else if (agreementsEntry === "checkout-retry" && sessionOffer) {
      initialStripeCheckout =
        premiumAvailability === "available"
          ? {
              offer: sessionOffer,
              // Omission means "resume the exact durable purchase contract,"
              // never consent to discard its Promotion Code or pay the regular
              // price.
              code: null,
              recoverySessionId: expiredCheckoutRecoverySessionId,
            }
          : null;
    } else {
      return { notFound: true };
    }
  }

  if (
    !stripeCheckoutEnabled &&
    checkoutCandidate &&
    !isConfiguredPremiumCheckoutPlan(checkoutCandidate.plan)
  ) {
    return { notFound: true };
  }

  if (
    !stripeCheckoutEnabled &&
    firstQueryValue(context.query.checkout) === "1" &&
    requestedPlan &&
    requestedPlan !== "free" &&
    !premiumAccessPlanKeys.includes(requestedPlan as PremiumAccessPlanKey) &&
    !checkoutCandidate
  ) {
    return { notFound: true };
  }

  // A.6: a resume open carries no checkout target, so resolve the signed-in
  // member's own plan and build its offer here. Without it the modal renders
  // null (it needs an offer or agreements) and the reopen never happens.
  let resumeOffer: PublicCheckoutOffer | null = null;
  if (firstQueryValue(context.query.resume) === "setup") {
    try {
      const [
        { default: getUserFromCtx },
        { defaultPremiumMembershipReaderDeps },
      ] = await Promise.all([
        import("~/utils/getUserFromCtx"),
        import("~/server/commerce/premiumMembership"),
      ]);
      const resumeUser = await getUserFromCtx(context);
      if (resumeUser) {
        const sub =
          await defaultPremiumMembershipReaderDeps.readNewestCommerceSubscription(
            resumeUser.id
          );
        const planKey = resumePlanKeyFromCanonical(sub?.canonicalPlan);
        if (planKey) resumeOffer = resumePremiumOffer(planKey);
      }
    } catch (error) {
      console.error(
        `[checkout] resume offer unavailable: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  // The visitor's own plan greys that card's CTA on the storefront ("Your
  // current plan"). The session read already carries the access flags; a
  // signed-out visitor (or an auth read failure) keeps live CTAs everywhere.
  let currentPlan: AccessPageProps["currentPlan"] = null;
  try {
    const { default: getUserFromCtx } = await import("~/utils/getUserFromCtx");
    const currentUser = await getUserFromCtx(context);
    if (currentUser) currentPlan = currentUser.isPremium ? "premium" : "free";
  } catch (error) {
    console.error(
      `[checkout] current plan read unavailable: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return {
    props: {
      signupCompleteHref: isReleaseHeld(releaseApprovedFor(context.req))
        ? "/signup-complete"
        : "/dashboard",
      openingMode,
      checkoutOrigin: whopCheckoutProxyOrigin(),
      checkoutEntries,
      publicCheckoutOffers,
      catalogUnavailable,
      resumeOffer,
      currentPlan,
      // Waitlist and closed requests stay on the matching Premium treatment.
      // Stripe targets never fall through to the legacy Whop iframe.
      initialCheckout:
        premiumAvailability !== "available" ||
        stripeCheckoutEnabled ||
        initialStripeCheckout ||
        initialStripeAgreements
          ? null
          : checkoutCandidate,
      stripeCheckoutEnabled,
      stripePublishableKey: stripePublishableKey(),
      // An agreements re-entry supersedes a checkout open for the same URL.
      initialStripeCheckout: initialStripeAgreements
        ? null
        : initialStripeCheckout,
      initialStripeAgreements,
      premiumAvailability,
    },
  };
};

function AccessPageStyles() {
  return (
    <style jsx global>{`
      .twt-access-shell {
        --bg-app: var(--twt-ground);
        --bg-canvas: var(--twt-canvas);
        --bg-card: var(--twt-card);
        --paper: var(--twt-hairline);
        --hairline: var(--twt-hairline);
        --hairline-hover: #d8d5c4;
        --ink: var(--twt-ink);
        --ink-soft: var(--twt-ink-soft);
        --graphite: var(--twt-graphite);
        --graphite-soft: var(--twt-graphite-soft);
        --clay: var(--twt-clay);
        --clay-deep: var(--twt-clay-deep);
        --sage: var(--twt-sage);
        --sage-deep: var(--twt-sage-deep);
        --sage-bg: var(--twt-sage-bg);
        background: var(--bg-app);
        color: var(--ink);
      }

      .twt-access-shell main {
        background: var(--bg-app);
      }

      .twt-access-main,
      .twt-access-main * {
        box-sizing: border-box;
      }

      .twt-access-main a {
        text-decoration: none;
      }

      .twt-access-main button {
        font: inherit;
      }

      .twt-access-main a:focus-visible,
      .twt-access-main button:focus-visible,
      .twt-access-main input:focus-visible,
      .twt-access-shell header a:focus-visible,
      .twt-access-shell header button:focus-visible {
        outline: 2px solid var(--clay);
        outline-offset: 3px;
      }

      /* MarketingNav on this page (owner: the header read as slop here). The
         shared chrome is built for the marketing-page cream; this shell uses
         the ground token with its own hairline, so the bar arrives in the
         wrong cream, on the wrong rule, and on a 1360px box that drifts off
         the page gutter as the viewport widens. Bring the bar onto this
         page's canvas, hairline, and gutter. */
      .twt-access-shell header {
        background: rgba(243, 241, 231, 0.95);
        border-bottom-color: var(--hairline);
      }

      .twt-access-shell header > div {
        max-width: 1676px;
        margin-inline: auto;
        padding-left: clamp(28px, 6vw, 88px);
        padding-right: clamp(28px, 6vw, 88px);
      }

      .twt-access-shell header nav[aria-label="Public navigation"] {
        color: var(--graphite);
      }

      .twt-access-shell header nav[aria-label="Public navigation"] a:hover {
        color: var(--ink);
      }

      /* The current page reads as current in the middle nav. */
      .twt-access-shell
        header
        nav[aria-label="Public navigation"]
        a[href="/pricing"] {
        color: var(--ink);
      }

      .access-hero {
        max-width: 980px;
        padding: 88px clamp(28px, 6vw, 88px) 56px;
      }

      .access-hero h1 {
        color: var(--ink);
        font-family: var(--twt-public-serif), Georgia, serif;
        font-size: clamp(52px, 7vw, 92px);
        font-variation-settings: "opsz" 144, "SOFT" 20;
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 0.96;
      }

      .access-hero h1 em {
        color: var(--graphite);
        font-style: italic;
        font-weight: 350;
      }

      .access-plans {
        padding: 8px clamp(28px, 6vw, 88px) 32px;
      }

      /* Header copy left, billing toggle centered on the section: the outer
         1fr tracks share the leftover width equally, so the auto-sized toggle
         sits on the section's midpoint regardless of header copy length. */
      .plans-head {
        align-items: center;
        border-bottom: 1px solid var(--hairline);
        display: grid;
        gap: 14px 20px;
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        margin-bottom: 24px;
        padding-bottom: 14px;
      }

      .plans-head .billing-toggle {
        grid-column: 2;
        justify-self: center;
      }

      .plans-head h2 {
        color: var(--ink);
        font-family: var(--twt-public-serif), Georgia, serif;
        font-size: 22px;
        font-weight: 450;
        letter-spacing: -0.01em;
        line-height: 1.2;
      }

      .plans-note {
        color: var(--graphite);
        font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas,
          monospace;
        font-size: 10.5px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
      }

      .plan-grid {
        display: grid;
        gap: 14px;
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }

      .plan-card {
        background: var(--bg-card);
        border: 1px solid var(--hairline);
        border-radius: 14px;
        display: flex;
        flex-direction: column;
        min-height: 400px;
        min-width: 0;
        padding: 28px 28px 24px;
        position: relative;
      }

      .plan-kicker-row {
        align-items: center;
        display: flex;
        gap: 8px;
        margin-bottom: 22px;
        min-height: 18px;
      }

      .plan-kicker {
        color: var(--ink-soft);
        font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas,
          monospace;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }

      /* The annual-view marker (Kimi reference logic: the badge appears only
         when the yearly cadence is selected). Sage chip, same as the paid
         state in the checkout agreements step. */
      .plan-badge {
        background: var(--sage-bg);
        border: 1px solid rgba(71, 90, 50, 0.28);
        border-radius: 6px;
        color: var(--sage-deep);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.04em;
        line-height: 16px;
        padding: 0 8px;
      }

      .plan-badge-closed {
        background: var(--twt-ground);
        border-color: var(--twt-hairline);
        color: var(--twt-graphite);
      }

      /* One fixed-height price block per card: headline row, then the
         billed-as subline. Equal heights keep the bullets and the Join
         buttons on one line across cards. */
      .plan-priceblock {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        margin-bottom: 18px;
        min-height: 76px;
      }

      .plan-price {
        align-items: baseline;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .plan-price .amount {
        color: var(--ink);
        font-family: var(--twt-public-serif), Georgia, serif;
        font-size: 52px;
        font-variation-settings: "opsz" 144;
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 1;
      }

      .plan-price .period {
        color: var(--graphite);
        font-size: 13.5px;
        font-weight: 400;
        line-height: 1.4;
      }

      /* Strikethrough anchor: the monthly rate, shown only when the selected
         cadence prices a month lower (Kimi reference: the anchor sits on the
         price line, one step quieter than the period). */
      .plan-anchor {
        color: var(--graphite-soft);
        font-size: 14px;
        line-height: 1.4;
        text-decoration: line-through;
      }

      .plan-billed {
        color: var(--graphite);
        font-size: 13px;
        line-height: 1.4;
        margin: 6px 0 0;
        min-height: 18px;
      }

      .plan-price-contact .amount-text {
        color: var(--ink);
        font-family: var(--twt-public-serif), Georgia, serif;
        font-size: 32px;
        font-variation-settings: "opsz" 144;
        font-style: italic;
        font-weight: 400;
        letter-spacing: -0.01em;
        line-height: 1.1;
      }

      /* Section-level billing switch (§101.3, tokens in place of hex). */
      .billing-toggle,
      .twtCadence {
        background: var(--twt-hairline);
        border-radius: var(--twt-radius-md);
        display: inline-grid;
        gap: 2px;
        grid-template-columns: repeat(3, minmax(0, auto));
        padding: 3px;
      }

      .billing-toggle button,
      .twtCadence button {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 7px;
        color: var(--twt-graphite);
        cursor: pointer;
        display: inline-flex;
        font: 600 14px/1 inherit;
        gap: 8px;
        justify-content: center;
        letter-spacing: 0.02em;
        min-height: 42px;
        min-width: 0;
        padding: 0 18px;
        transition: background 120ms ease, color 120ms ease;
        white-space: nowrap;
      }

      .billing-toggle button[aria-pressed="true"],
      .billing-toggle button[data-selected],
      .twtCadence button[data-selected] {
        background: var(--twt-card);
        box-shadow: 0 1px 2px rgba(20, 20, 19, 0.08),
          inset 0 0 0 1px rgba(20, 20, 19, 0.07);
        color: var(--twt-ink);
      }

      .billing-toggle
        button:hover:not([aria-pressed="true"]):not([data-selected]) {
        color: var(--twt-ink);
      }

      /* Savings note on the longest-cadence segment. $349 * 12 = $4,188
         versus $3,300 yearly is $888. */
      .billing-save,
      .twtCadenceBadge {
        background: var(--twt-sage-bg);
        border: 1px solid rgba(71, 90, 50, 0.2);
        border-radius: 999px;
        color: var(--twt-sage-deep);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        line-height: 1.15;
        padding: 2px 7px;
      }

      .plan-bullets {
        display: flex;
        flex-direction: column;
        gap: 11px;
        list-style: none;
        margin: 0 0 20px;
        padding: 0;
      }

      .plan-bullets li {
        align-items: flex-start;
        color: var(--ink-soft);
        display: flex;
        font-size: 13.5px;
        gap: 10px;
        line-height: 1.45;
      }

      .plan-bullets li::before {
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%23788C5D' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><polyline points='3 8.5 6.5 12 13 4.5'/></svg>");
        background-position: center;
        background-repeat: no-repeat;
        background-size: 14px;
        content: "";
        flex-shrink: 0;
        height: 14px;
        margin-top: 3px;
        width: 14px;
      }

      /* Button + one reserved status line in every card. The reserved line
         (72px block minimum) is what keeps the Join buttons on one line
         across cards whose under-button text differs or is absent. */
      .plan-cta {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: auto;
        min-height: 72px;
      }

      .access-button {
        align-items: center;
        background: var(--ink);
        border: 1px solid var(--ink);
        border-radius: 8px;
        color: var(--bg-canvas);
        display: inline-flex;
        font-size: 13.5px;
        font-weight: 500;
        gap: 6px;
        justify-content: center;
        min-height: 44px;
        padding: 0 16px;
        transition: background 120ms ease;
        width: 100%;
      }

      .access-button:hover:not(:disabled) {
        background: var(--ink-soft);
      }

      .access-button:disabled {
        cursor: not-allowed;
        opacity: 1;
      }

      .access-button.secondary {
        background: var(--bg-card);
        border-color: var(--hairline-hover);
        color: var(--ink);
      }

      .access-button.secondary:hover {
        background: var(--paper);
        border-color: var(--graphite-soft);
      }

      .access-button .arrow,
      .cta-arrow {
        color: var(--clay);
        margin-left: 4px;
      }

      /* The one under-button treatment (owner, 2026-07-31: under-button
         prose is useful info or gone, and what survives is styled as one
         system, the billed line a step above old fine print): 13px on
         graphite, up from 11.5px on graphite-soft. Both steps adjustable.
         display:block + min-height reserves the line even when empty, so
         buttons stay level across cards. */
      .plan-fine {
        color: var(--graphite);
        display: block;
        font-size: 13px;
        line-height: 1.4;
        min-height: 18px;
        text-align: center;
      }

      .checkout-opening-status,
      .checkout-waitlist-confirmation {
        background: var(--paper);
        border: 1px solid var(--hairline-hover);
        border-radius: 8px;
        color: var(--ink-soft);
        display: flex;
        flex-direction: column;
        font-size: 13px;
        gap: 4px;
        line-height: 1.45;
        min-height: 64px;
        padding: 12px 14px;
      }

      .checkout-opening-status strong {
        color: var(--ink);
        font-size: 14px;
        font-weight: 600;
      }

      .checkout-waitlist-confirmation {
        background: var(--sage-bg);
        border-color: rgba(71, 90, 50, 0.28);
        color: var(--sage-deep);
        justify-content: center;
      }

      .checkout-waitlist-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .checkout-waitlist-form label {
        color: var(--ink-soft);
        display: flex;
        flex-direction: column;
        font-size: 12px;
        font-weight: 600;
        gap: 6px;
      }

      .checkout-waitlist-form label > span {
        align-items: baseline;
        display: flex;
        gap: 6px;
      }

      .checkout-waitlist-form small {
        color: var(--graphite);
        font-size: 11px;
        font-weight: 400;
      }

      .checkout-waitlist-form input {
        background: var(--bg-card);
        border: 1px solid var(--hairline-hover);
        border-radius: 8px;
        color: var(--ink);
        font: inherit;
        font-size: 14px;
        min-height: 44px;
        padding: 0 12px;
        width: 100%;
      }

      .checkout-waitlist-form input:focus-visible {
        border-color: var(--clay);
        outline: 2px solid rgba(217, 119, 87, 0.22);
        outline-offset: 1px;
      }

      .checkout-waitlist-form input:disabled {
        background: var(--paper);
      }

      .checkout-waitlist-error {
        color: #8a2a18;
        font-size: 12px;
        line-height: 1.45;
      }

      .faq {
        border-top: 1px solid var(--hairline);
        margin: 32px auto 0;
        max-width: 980px;
        padding: 48px 0 100px;
        width: calc(100% - 2 * clamp(28px, 6vw, 88px));
      }

      .faq h2 {
        color: var(--ink);
        font-family: var(--twt-public-serif), Georgia, serif;
        font-size: 32px;
        font-variation-settings: "opsz" 144;
        font-weight: 400;
        letter-spacing: -0.015em;
        line-height: 1.2;
        margin: 0 0 24px;
      }

      .faq a,
      .faq .faq-link {
        border-bottom: 1px solid rgba(180, 93, 63, 0.3);
        color: var(--clay-deep);
      }

      /* An in-text link that opens the Max application dialog, the same one
         the Max card's button opens. */
      .faq .faq-link {
        background: none;
        border-top: 0;
        border-left: 0;
        border-right: 0;
        cursor: pointer;
        font: inherit;
        padding: 0;
      }

      .faq a:hover,
      .faq .faq-link:hover {
        border-bottom-color: var(--clay-deep);
      }

      .faq .faq-link:focus-visible {
        outline: 2px solid var(--clay-deep);
        outline-offset: 2px;
      }

      .faq-list {
        border-top: 1px solid var(--hairline);
      }

      .faq-list details {
        border-bottom: 1px solid var(--hairline);
        padding: 22px 0;
      }

      .faq-list summary {
        align-items: center;
        color: var(--ink);
        cursor: pointer;
        display: flex;
        font-size: 17px;
        font-weight: 600;
        gap: 16px;
        justify-content: space-between;
        line-height: 1.4;
        list-style: none;
        min-height: 32px;
      }

      .faq-list summary:focus-visible {
        outline: 2px solid var(--clay);
        outline-offset: 4px;
      }

      .faq-list summary::-webkit-details-marker {
        display: none;
      }

      .faq-list summary::after {
        color: var(--clay-deep);
        content: "+";
        font-family: "Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas,
          monospace;
        font-size: 20px;
        font-weight: 400;
      }

      .faq-list details[open] summary::after {
        content: "-";
      }

      .faq-list details p {
        color: var(--graphite);
        font-size: 15px;
        line-height: 1.6;
        margin-top: 16px;
        max-width: 680px;
      }

      @media (max-width: 900px) {
        .access-hero {
          padding: 56px 24px 40px;
        }

        .access-plans {
          padding-left: 24px;
          padding-right: 24px;
        }

        .faq {
          width: calc(100% - 48px);
        }

        .twt-access-shell header > div {
          padding-left: 24px;
          padding-right: 24px;
        }

        .plans-head {
          gap: 16px;
        }
      }

      @media (max-width: 1100px) {
        .plan-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 640px) {
        .access-hero {
          padding: 52px 16px 36px;
        }

        .access-plans {
          padding-left: 16px;
          padding-right: 16px;
        }

        .faq {
          width: calc(100% - 32px);
          padding-bottom: 72px;
        }

        .plan-grid {
          grid-template-columns: minmax(0, 1fr);
        }

        .plans-head {
          grid-template-columns: minmax(0, 1fr);
        }

        .plans-head .billing-toggle,
        .plans-head .twtCadence {
          display: flex;
          grid-column: 1;
          width: 100%;
        }

        .plans-head .billing-toggle button,
        .plans-head .twtCadence button {
          flex: 1 1 0;
          padding-left: 8px;
          padding-right: 8px;
        }

        .plan-card {
          min-height: 360px;
          padding: 26px 24px 22px;
        }

        .twt-access-shell header > div {
          gap: 8px;
          padding-left: 16px;
          padding-right: 16px;
        }

        .twt-access-shell header a[aria-label="Trade with Titans home"] > span {
          gap: 8px;
        }

        .twt-access-shell
          header
          a[aria-label="Trade with Titans home"]
          > span
          > span:last-child {
          font-size: 18px;
        }

        .twt-access-shell .twt-member-cta--access {
          padding-left: 12px;
          padding-right: 12px;
        }

        .twt-access-shell header button[aria-label="More access options"] {
          padding-left: 10px;
          padding-right: 10px;
        }
      }
    `}</style>
  );
}

function premiumCtaUsesClosedTreatment(
  availability: OfferAvailability | null
): boolean {
  // P12.4: waitlist renders the §101.2 waitlist CTA variant (At capacity /
  // Reserve access / "$0 today. We'll email you before activation."), which
  // opens the same checkout modal; the server decides the reservation flow.
  // Only closed (or a missing offer row) uses the closed treatment.
  return availability === "closed" || availability === null;
}

const Page: NextPage<AccessPageProps> = ({
  signupCompleteHref = "/dashboard",
  openingMode,
  checkoutOrigin,
  checkoutEntries,
  publicCheckoutOffers,
  catalogUnavailable,
  initialCheckout,
  stripeCheckoutEnabled,
  stripePublishableKey: publishableKey,
  initialStripeCheckout,
  initialStripeAgreements,
  premiumAvailability,
  resumeOffer,
  currentPlan,
}) => {
  const router = useRouter();
  const newsletterVerifyOpen =
    router.isReady && router.query.newsletterVerify === "1";
  const closeNewsletterVerify = React.useCallback(() => {
    if (!router.isReady) return;
    const nextQuery = { ...router.query };
    delete nextQuery.newsletterVerify;
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router]);
  const [newsletterStartMounted, setNewsletterStartMounted] =
    React.useState(false);
  const [newsletterStartOpen, setNewsletterStartOpen] = React.useState(false);
  const [newsletterSource, setNewsletterSource] = React.useState("access-free");
  const [newsletterEmail, setNewsletterEmail] = React.useState("");
  React.useEffect(() => {
    if (!router.isReady || router.query.newsletterSignup !== "1") return;
    const requestedSource = router.query.newsletterSource;
    setNewsletterSource(
      typeof requestedSource === "string" &&
        /^[a-zA-Z0-9_./:-]{1,120}$/.test(requestedSource)
        ? requestedSource
        : "access-free"
    );
    const requestedEmail = router.query.newsletterEmail;
    setNewsletterEmail(
      typeof requestedEmail === "string" && requestedEmail.includes("@")
        ? requestedEmail.slice(0, 254)
        : ""
    );
    setNewsletterStartMounted(true);
    setNewsletterStartOpen(true);
    const nextQuery = { ...router.query };
    delete nextQuery.newsletterSignup;
    delete nextQuery.newsletterSource;
    delete nextQuery.newsletterEmail;
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router.isReady, router.query.newsletterSignup, router]);
  const [preferencesMounted, setPreferencesMounted] = React.useState(false);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);
  const [preferencesContinue, setPreferencesContinue] = React.useState(false);
  // Max (the lifetime tier), Team, and Enterprise open the inquiry dialog in
  // place of the separate /lifetime and /contact-us pages. Mounted on first
  // open, like the newsletter dialog, so the page bundle stays small.
  const [inquiryMounted, setInquiryMounted] = React.useState(false);
  const [inquiryPlan, setInquiryPlan] =
    React.useState<AccessInquiryPlan | null>(null);
  const openInquiry = React.useCallback((plan: AccessInquiryPlan) => {
    setInquiryMounted(true);
    setInquiryPlan(plan);
  }, []);
  React.useEffect(() => {
    if (!router.isReady || router.query.newsletterPreferences !== "1") return;
    setPreferencesContinue(router.query.newsletterContinue === "1");
    setPreferencesMounted(true);
    setPreferencesOpen(true);
    const nextQuery = { ...router.query };
    delete nextQuery.newsletterPreferences;
    delete nextQuery.newsletterContinue;
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router.isReady, router.query.newsletterPreferences, router]);
  const defaultOffer =
    publicCheckoutOffers.find((offer) => offer.isDefault) ??
    publicCheckoutOffers[0] ??
    null;
  const [selectedPremiumOffer, setSelectedPremiumOffer] =
    React.useState<PublicCheckoutOffer | null>(
      initialStripeCheckout?.offer ??
        initialStripeAgreements?.offer ??
        defaultOffer
    );
  const [activeCheckout, setActiveCheckout] =
    React.useState<CheckoutTarget | null>(null);
  const [stripeCheckout, setStripeCheckout] =
    React.useState<StripeCheckoutRequest | null>(initialStripeCheckout);
  const [stripeAgreements, setStripeAgreements] = React.useState(
    initialStripeAgreements
  );
  // A.6: ?resume=setup opens the sheet at the first unfinished setup room
  // (from onboarding task state). The dashboard reminder bar and the login
  // landing both link here until every room is done.
  const resumeSetup = router.query.resume === "setup";
  const stripeCheckoutOpen =
    Boolean(stripeCheckout) || Boolean(stripeAgreements) || resumeSetup;
  const [stripeCheckoutMounted, setStripeCheckoutMounted] =
    React.useState(stripeCheckoutOpen);
  const [checkoutMounted, setCheckoutMounted] =
    React.useState(Boolean(activeCheckout));
  // Download each checkout only when it opens. Keep it mounted after the
  // first open so its existing close and reopen effects still run.
  React.useEffect(() => {
    if (stripeCheckoutOpen) setStripeCheckoutMounted(true);
  }, [stripeCheckoutOpen]);
  React.useEffect(() => {
    if (activeCheckout) setCheckoutMounted(true);
  }, [activeCheckout]);
  const checkoutQueryTimeoutRef = React.useRef<number | null>(null);

  const syncQuery = React.useCallback(
    (target: CheckoutTarget | null) => {
      if (!router.isReady) return;

      const nextQuery = { ...router.query };

      if (target) {
        nextQuery.checkout = "1";
        nextQuery.plan = target.plan;
      } else {
        delete nextQuery.checkout;
        delete nextQuery.plan;
        delete nextQuery.checkout_url;
      }

      void router.replace(
        { pathname: router.pathname, query: nextQuery },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router]
  );

  const openCheckout = React.useCallback(
    (target: CheckoutTarget) => {
      const { url } = target;
      if (!url?.length) return;
      if (checkoutQueryTimeoutRef.current) {
        window.clearTimeout(checkoutQueryTimeoutRef.current);
      }
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `twt_checkout_return=1; Path=/; Max-Age=1800; SameSite=Lax${secure}`;
      setActiveCheckout(target);
      checkoutQueryTimeoutRef.current = window.setTimeout(() => {
        syncQuery(target);
        checkoutQueryTimeoutRef.current = null;
      }, 250);
    },
    [syncQuery]
  );

  const closeCheckout = React.useCallback(() => {
    if (checkoutQueryTimeoutRef.current) {
      window.clearTimeout(checkoutQueryTimeoutRef.current);
      checkoutQueryTimeoutRef.current = null;
    }
    setActiveCheckout(null);
    syncQuery(null);
  }, [syncQuery]);

  React.useEffect(() => {
    return () => {
      if (checkoutQueryTimeoutRef.current) {
        window.clearTimeout(checkoutQueryTimeoutRef.current);
      }
    };
  }, []);

  const closeStripeCheckout = React.useCallback(() => {
    setStripeCheckout(null);
    setStripeAgreements(null);
    if (!router.isReady) return;
    const nextQuery = { ...router.query };
    delete nextQuery.checkout;
    delete nextQuery.plan;
    delete nextQuery.checkout_url;
    delete nextQuery.code;
    delete nextQuery.agreements;
    delete nextQuery.session_id;
    delete nextQuery.docusign_completion;
    delete nextQuery.resume;
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router]);

  React.useEffect(() => {
    const queryTarget = firstQueryValue(router.query.plan);
    if (!queryTarget) return;
    const queryOffer = publicCheckoutOffers.find(
      (offer) => offer.key === queryTarget || offer.slug === queryTarget
    );
    if (queryOffer) setSelectedPremiumOffer(queryOffer);
  }, [publicCheckoutOffers, router.query.plan]);

  // Keep the tier selector in sync with a resolver-opened Stripe checkout.
  React.useEffect(() => {
    if (initialStripeCheckout) {
      setSelectedPremiumOffer(initialStripeCheckout.offer);
    } else if (initialStripeAgreements?.offer) {
      setSelectedPremiumOffer(initialStripeAgreements.offer);
    }
  }, [initialStripeCheckout, initialStripeAgreements]);

  React.useEffect(() => {
    if (!router.isReady) return;
    // The Stripe modal owns every premium checkout open when Stripe is active.
    if (stripeCheckoutEnabled && !initialCheckout) return;
    const externalTargetRequested = [
      router.query.plan,
      router.query.checkout,
      router.query.checkout_url,
    ].some((value) => {
      const queryValue = firstQueryValue(value);
      return Boolean(
        initialCheckout && queryValue?.includes(initialCheckout.plan)
      );
    });
    const externalTarget = externalTargetRequested ? initialCheckout : null;

    if (externalTarget) {
      setActiveCheckout((current) => {
        if (
          current?.plan === externalTarget.plan &&
          current.url === externalTarget.url &&
          current.title === externalTarget.title
        ) {
          return current;
        }
        return externalTarget;
      });
      return;
    }

    if (router.query.checkout !== "1") return;
    if (router.query.plan === "free" || router.query.plan === "lifetime") {
      return;
    }
    if (!isPremiumAccessPlanKey(router.query.plan)) return;

    const target = checkoutEntries[router.query.plan];
    setActiveCheckout((current) => {
      if (
        current?.plan === target.plan &&
        current.url === target.url &&
        current.title === target.title
      ) {
        return current;
      }
      return target;
    });
  }, [
    checkoutEntries,
    initialCheckout,
    router.isReady,
    router.query.checkout,
    router.query.checkout_url,
    router.query.plan,
    router,
  ]);

  // One unit across every billing period (owner's same-unit rule, per-month
  // direction): the card headline is the derived monthly cost at the selected
  // cadence; the subline (billingNote) carries the actual charge and when it
  // is billed.
  const selectedOfferKey = selectedPremiumOffer?.key;
  const selectedLegacyPremiumKey = premiumAccessPlanKeys.find(
    (key) => key === selectedOfferKey
  );
  const selectedPremiumCheckout = selectedLegacyPremiumKey
    ? checkoutEntries[selectedLegacyPremiumKey]
    : null;
  const selectedPremiumOfferIsPublished = Boolean(
    selectedPremiumOffer &&
      publicCheckoutOffers.some(
        (offer) =>
          offer.key === selectedPremiumOffer.key &&
          offer.offerVersionMarker === selectedPremiumOffer.offerVersionMarker
      )
  );
  // Waitlist runs only through the Stripe modal (the reservation flow is a
  // Stripe setup-mode session); with the legacy provider it falls back to the
  // closed treatment rather than a dead-end modal.
  const premiumWaitlistMode =
    premiumAvailability === "waitlist" && stripeCheckoutEnabled;
  const premiumCtaClosed =
    premiumCtaUsesClosedTreatment(premiumAvailability) ||
    (premiumAvailability === "waitlist" && !stripeCheckoutEnabled);
  const premiumCheckoutReady =
    !premiumCtaClosed &&
    !premiumWaitlistMode &&
    !catalogUnavailable &&
    selectedPremiumOfferIsPublished &&
    (stripeCheckoutEnabled || Boolean(selectedPremiumCheckout?.url));
  const premiumWaitlistReady =
    premiumWaitlistMode &&
    !catalogUnavailable &&
    selectedPremiumOfferIsPublished;

  // ── Reference mechanics (Kimi pricing page), derived from the resolved
  // offers, never typed as literals ─────────────────────────────────────────
  // The anchor price is the monthly cadence's per-month rate; it shows struck
  // through only when the selected cadence prices a month lower. The savings
  // label on the longest-cadence segment is that cadence's discount against
  // paying monthly for the same span, as a percentage. The savings line in
  // the Premium kicker row states the selected cadence's saving in dollars.
  // The availability ribbon is independent of the selected billing period.
  const monthlyOffer =
    publicCheckoutOffers.find(
      (offer) =>
        offer.billingInterval === "month" && offer.billingIntervalCount === 1
    ) ?? null;
  const monthlyRateCents = monthlyOffer
    ? Math.round(monthlyOffer.amountCents / monthlyOffer.monthsPerCycle)
    : null;
  const selectedRateCents = selectedPremiumOffer
    ? Math.round(
        selectedPremiumOffer.amountCents / selectedPremiumOffer.monthsPerCycle
      )
    : null;
  const anchorAmount =
    monthlyRateCents !== null &&
    selectedRateCents !== null &&
    selectedRateCents < monthlyRateCents
      ? formatUsdCents(monthlyRateCents)
      : null;
  const longestCycle = publicCheckoutOffers.length
    ? Math.max(...publicCheckoutOffers.map((offer) => offer.monthsPerCycle))
    : 0;
  // The longest cadence's chip rides its segment in the Premium card's billing
  // pill whether or not that cadence is the selected one.
  const longestOffer =
    publicCheckoutOffers.find(
      (offer) => offer.monthsPerCycle === longestCycle
    ) ?? null;

  function savedAgainstMonthlyCents(option: PublicCheckoutOffer): number {
    if (monthlyRateCents === null) return 0;
    return monthlyRateCents * option.monthsPerCycle - option.amountCents;
  }

  function savingsChipFor(option: PublicCheckoutOffer): string | null {
    if (monthlyRateCents === null) return null;
    if (option.monthsPerCycle !== longestCycle || longestCycle <= 1) {
      return null;
    }
    const saved = savedAgainstMonthlyCents(option);
    if (saved <= 0) return null;
    const percent = Math.round(
      (saved / (monthlyRateCents * option.monthsPerCycle)) * 100
    );
    return percent > 0 ? `Save ${percent}%` : null;
  }

  // "Save $888 a year" / "Save $147 a quarter", from the offer's own period
  // ("/ year", "/ quarter", "/ 2 years"); nothing when the cadence saves
  // nothing against paying monthly.
  function savingsLineFor(option: PublicCheckoutOffer): string | null {
    const saved = savedAgainstMonthlyCents(option);
    if (saved <= 0) return null;
    const span = option.period.replace(/^\/\s*/, "").trim();
    if (!span) return null;
    const spanPhrase = /^\d/.test(span) ? `every ${span}` : `a ${span}`;
    return `Save ${formatUsdCents(saved)} ${spanPhrase}`;
  }

  const startPremiumCheckout = React.useCallback(() => {
    if (premiumCtaClosed) return;
    if (catalogUnavailable || !selectedPremiumOfferIsPublished) return;
    if (!selectedPremiumOffer) return;
    if (stripeCheckoutEnabled) {
      setStripeCheckout({
        offer: selectedPremiumOffer,
        code: null,
        recoverySessionId: null,
      });
      return;
    }
    if (selectedPremiumCheckout) openCheckout(selectedPremiumCheckout);
  }, [
    premiumCtaClosed,
    catalogUnavailable,
    selectedPremiumOfferIsPublished,
    stripeCheckoutEnabled,
    selectedPremiumOffer,
    selectedPremiumCheckout,
    openCheckout,
  ]);

  return (
    <>
      <HeadComponent title={pageTitle} />
      <Head>
        <link rel="dns-prefetch" href={checkoutOrigin} />
        <link rel="preconnect" href={checkoutOrigin} />
      </Head>
      <div
        className={`${accessSerif.variable} ${publicPageShellClassName} twt-access-shell ${pricing.page}`}
      >
        <AccessPageStyles />
        <MarketingNav memberAccessHeld={signupCompleteHref === "/signup-complete"} />
        <main className="twt-access-main">
          <PricingPlans
            offers={publicCheckoutOffers}
            selectedOffer={selectedPremiumOffer}
            onSelectOffer={setSelectedPremiumOffer}
            anchorAmount={anchorAmount}
            savings={longestOffer ? savingsChipFor(longestOffer) : null}
            savingsLine={null}
            currentPlan={currentPlan}
            initialView={router.query.view === "team" ? "team" : "individual"}
            premiumRibbon={premiumCtaClosed ? null : "Limited supply"}
            actions={{
              free: (
                <NewsletterSignupLink
                  source="access-free"
                  className="access-button"
                >
                  Get started <span className="arrow">→</span>
                </NewsletterSignupLink>
              ),
              premium: premiumCtaClosed ? (
                <div className="checkout-opening-status" role="status">
                  <strong>Currently unavailable</strong>
                  <span>
                    Pro checkout is temporarily closed. This page will update
                    when it reopens.
                  </span>
                </div>
              ) : premiumWaitlistMode && premiumWaitlistReady ? (
                <button
                  type="button"
                  className="access-button"
                  onClick={startPremiumCheckout}
                >
                  Get started{" "}
                  <span className="arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              ) : (
                <CheckoutOpeningControl
                  mode="buyable"
                  buttonLabel="Get started"
                  offerKey={selectedPremiumOffer?.key ?? null}
                  buyable={premiumCheckoutReady}
                  billingNote=""
                  unavailableMessage="Checkout is temporarily unavailable"
                  source={router.asPath.split(/[?#]/)[0] || "/get-access"}
                  onBuy={startPremiumCheckout}
                />
              ),
              lifetime: (
                <button
                  type="button"
                  className="access-button secondary"
                  onClick={() => openInquiry("lifetime")}
                >
                  Apply for Max <span className="cta-arrow">→</span>
                </button>
              ),
              team: (
                <Link
                  href="/contact-sales?plan=team"
                  className="access-button secondary"
                >
                  Contact sales <span className="cta-arrow">→</span>
                </Link>
              ),
              enterprise: (
                <Link
                  href="/contact-sales?plan=enterprise"
                  className="access-button secondary"
                >
                  Contact sales <span className="cta-arrow">→</span>
                </Link>
              ),
            }}
          />

          <section className="faq" aria-labelledby="pricing-faq-title">
            <h2 id="pricing-faq-title">FAQ</h2>
            <div className="faq-list">
              {premiumAvailability === "available" ? (
                <details>
                  <summary>When will I be charged for Pro?</summary>
                  <p>
                    Choose monthly, quarterly, or yearly billing. You pay for
                    the selected period when you complete checkout, and your
                    subscription renews on that schedule until you cancel.
                  </p>
                </details>
              ) : (
                <details>
                  <summary>How does the Pro waitlist work?</summary>
                  <p>
                    Joining the waitlist saves your place in line and starts Free
                    right away. You pick a billing period, save a card, and sign a
                    short reservation agreement, but nothing is charged until your
                    spot opens, and we email you before that first charge. You can
                    cancel the reservation from your Account page any time before
                    then. How long you wait depends on our capacity. We hold a
                    high bar for every member's experience, and we only add
                    members when we can meet it without raising prices. We are
                    also adding capacity quickly.
                  </p>
                </details>
              )}
              <details>
                <summary>Can I change my billing period or cancel?</summary>
                <p>
                  To switch between monthly, quarterly, and yearly billing, go
                  to Account and choose Manage plan. The new billing period
                  starts at your next billing cycle. If you are on a legacy rate
                  or a promotion,{" "}
                  <Link href="/support">contact support</Link> and we will help
                  you switch. You can cancel from Account at any time and keep
                  access through the period you already paid for. Charges
                  already made are not refunded except where the law requires
                  it.
                </p>
              </details>
              <details>
                <summary>
                  What payment methods do you accept, and are taxes included?
                </summary>
                <p>
                  We accept card payments through Stripe, including Apple Pay
                  and Google Pay, and all prices are in US dollars. Sales tax or
                  VAT is calculated at checkout from your billing address and
                  shown on your invoice. If your card is not in US dollars, your
                  bank may add a conversion fee.
                </p>
              </details>
              <details>
                <summary>How does Max work?</summary>
                <p>
                  Max is lifetime access for one price, and that price covers
                  every feature we add to Max. Lifetime means for as long as TWT
                  operates the service. It starts with a short{" "}
                  <button
                    type="button"
                    className="faq-link"
                    onClick={() => openInquiry("lifetime")}
                  >
                    Max application
                  </button>
                  . After you apply, a TWT representative will personally walk
                  you through setup.
                </p>
              </details>
              <details>
                <summary>Should I choose Team or Enterprise?</summary>
                <p>
                  Team fits a group of 2 to 150 people who want Pro on every
                  seat with central billing and SSO, priced per seat. Enterprise
                  fits organizations that need a dedicated tenant, their own
                  cloud, or an on-premises deployment, or technical support from
                  the TWT team during onboarding or on an ongoing basis.{" "}
                  <Link href="/contact-sales?plan=enterprise">
                    Contact sales
                  </Link>{" "}
                  and we will reply by email.
                </p>
              </details>
              <details>
                <summary>How is my data handled?</summary>
                <p>
                  We store what you give us, such as your name, email, and for
                  paid plans your billing address, and your card is stored with
                  our payment processor, Stripe. The{" "}
                  <Link href="/terms">Terms of Service and Privacy Policy</Link>{" "}
                  explain how we use and share account data. TWT does not connect
                  to brokerage accounts today. If we add that option, we will
                  make it very clear.
                </p>
              </details>
            </div>
          </section>
        </main>
        <MarketingFooter />

        {(stripeCheckoutMounted || stripeCheckoutOpen) &&
        (openingMode === "buyable" || initialStripeAgreements || resumeSetup) ? (
          <StripeCheckoutModal
            signupCompleteHref={signupCompleteHref}
            offers={publicCheckoutOffers}
            open={stripeCheckoutOpen}
            offer={
              stripeAgreements?.offer ??
              stripeCheckout?.offer ??
              (resumeSetup ? resumeOffer : null)
            }
            code={stripeCheckout?.code ?? null}
            recoverySessionId={stripeCheckout?.recoverySessionId ?? null}
            publishableKey={publishableKey}
            initialAgreements={stripeAgreements}
            initialResume={resumeSetup && !stripeAgreements && !stripeCheckout}
            waitlist={
              stripeAgreements?.reservation === true ||
              (!stripeAgreements && premiumAvailability === "waitlist")
            }
            onClose={closeStripeCheckout}
          />
        ) : null}
      </div>

      {(checkoutMounted || activeCheckout) && openingMode === "buyable" ? (
        <CheckoutModal
          open={Boolean(activeCheckout)}
          checkoutUrl={activeCheckout?.url ?? ""}
          title={activeCheckout?.title ?? "Checkout"}
          onClose={closeCheckout}
        />
      ) : null}
      {preferencesMounted ? (
        <NewsletterPreferencesModal
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
          continueAfterVerification={preferencesContinue}
        />
      ) : null}
      {newsletterStartMounted ? (
        <NewsletterStartModal
          key={`${newsletterSource}:${newsletterEmail}`}
          open={newsletterStartOpen}
          onClose={() => setNewsletterStartOpen(false)}
          source={newsletterSource}
          initialEmail={newsletterEmail}
        />
      ) : null}
      {newsletterVerifyOpen ? (
        <NewsletterVerifyModal open onClose={closeNewsletterVerify} />
      ) : null}
      {inquiryMounted ? (
        <AccessInquiryModal
          open={inquiryPlan !== null}
          plan={inquiryPlan}
          onClose={() => setInquiryPlan(null)}
        />
      ) : null}
    </>
  );
};

export default Page;
